import React, { useMemo, useState } from 'react';
import { DatePicker, Form, Input, InputNumber, Message, Radio, RadioGroup, Tag } from 'rsuite';
import { Button, ConfirmModal, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { showToast } from '../utils/toast';
import PageHeader from '../components/shared/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { PAYROLL_ENABLED, PAYROLL_PERMISSION } from '../config/payrollFeature';
import { useAddPatoPolicy, usePatoPolicies, useRemovePatoPolicy } from '../hooks/useQueries';

// Time off settings for the whole company: the PATO policy (the annual grant
// everybody is preloaded with, as a dated history).
//
// A policy in force on 1 January is that year's grant. One that starts later in
// the year posts the difference on its date - a raise from 80 to 120 hours on
// 1 July shows on every ledger as a +40 credit that day - so a new policy is
// added with the date it takes effect rather than by overwriting the current
// one. Unlimited time off is a policy like any other: from its date there is no
// balance, though what people use is still recorded.
//
// Policies are never edited, only added or removed, for the same reason the
// ledger they feed is never edited. Per-employee adjustments are not here; they
// are on each employee's record.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatIsoDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!match) return '';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTimestamp = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-US') : '';
};

const describePolicy = (policy) => (
  policy.unlimited ? 'Unlimited time off' : `${Number(policy.hours).toFixed(2)} hours a year`
);

// The next 1 January - when a policy change most usually lands.
const nextNewYear = () => new Date(new Date().getFullYear() + 1, 0, 1);

function PatoPolicies() {
  const policiesQuery = usePatoPolicies();
  const addMutation = useAddPatoPolicy();
  const removeMutation = useRemovePatoPolicy();

  const [draft, setDraft] = useState(null);
  const draftShown = useLastPresent(draft); // keeps the dialog steady while it fades out
  const [removing, setRemoving] = useState(null);

  const isFallback = Boolean(policiesQuery.data?.fallback);
  const currentId = policiesQuery.data?.currentId ?? null;
  // Newest first, so the one most likely to be asked about is on top.
  const rows = useMemo(() => [...(policiesQuery.data?.policies || [])].reverse(), [policiesQuery.data]);
  const todayIso = toIsoDate(new Date());

  const openNew = () => {
    const current = rows.find((row) => row.id === currentId) || rows[0];
    setDraft({
      effectiveDate: nextNewYear(),
      mode: 'hours',
      hours: current && !current.unlimited ? current.hours : 80,
      notes: '',
    });
  };

  const hoursNumber = Number(draft?.hours);
  const draftValid = Boolean(draft?.effectiveDate)
    && (draft?.mode === 'unlimited' || (Number.isFinite(hoursNumber) && hoursNumber >= 0 && hoursNumber <= 2000));

  const submitDraft = async () => {
    if (!draftValid) return;
    const unlimited = draft.mode === 'unlimited';
    try {
      await addMutation.mutateAsync({
        effectiveDate: toIsoDate(draft.effectiveDate),
        unlimited,
        hours: unlimited ? null : hoursNumber,
        notes: draft.notes.trim() || null,
      });
      showToast('success', 'PATO policy added.');
      setDraft(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to save the policy'));
    }
  };

  const submitRemove = async () => {
    if (!removing) return;
    try {
      await removeMutation.mutateAsync(removing.id);
      showToast('success', 'PATO policy removed.');
      setRemoving(null);
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to remove the policy'));
    }
  };

  // The only policy cannot be removed - every balance would quietly fall back
  // to the settings default - so the action is not offered for it.
  const canRemove = !isFallback && rows.length > 1;

  return (
    <>
      <p className="text-muted">
        Everyone is preloaded with the PATO policy in force on January 1 as that year&rsquo;s grant. To change
        it, add a policy with the date it takes effect. A change dated later in the year credits or debits
        the difference on that date. Choose unlimited time off to stop tracking a balance; time off and sick
        time are still recorded as used. Adjustments for one employee are made on their record.
      </p>

      {isFallback ? (
        <Message type="warning" showIcon style={{ marginBottom: 16 }}>
          No dated PATO policy is stored yet, so everybody is on the single default below. Run
          server/sql/payroll_pato_ledger.sql against the database to start the history.
        </Message>
      ) : null}

      <div className="nsi-table-wrap">
        <div className="bdc-table-head">
          <div className="bdc-table-head__heading">
            <h3 className="bdc-table-head__title">PATO Policy History</h3>
            <span className="bdc-count">{isFallback ? 0 : rows.length}</span>
          </div>
          <div className="bdc-table-head__actions">
            <button
              type="button"
              className="bdc-btn bdc-btn--sm bdc-btn--primary"
              onClick={openNew}
              disabled={isFallback || policiesQuery.isLoading}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Add policy
            </button>
          </div>
        </div>

        {policiesQuery.error ? (
          <Message type="error" showIcon>
            {String(policiesQuery.error?.message || policiesQuery.error)}
          </Message>
        ) : null}

        <table className="nsi-table bdc-table--narrow">
          <thead>
            <tr>
              <th style={{ width: 190, whiteSpace: 'nowrap' }}>Effective</th>
              <th style={{ width: 190 }}>Policy</th>
              <th>Notes</th>
              <th style={{ width: 200 }}>Entered by</th>
              {canRemove ? <th style={{ width: 90 }} aria-label="Actions" /> : null}
            </tr>
          </thead>
          <tbody>
            {policiesQuery.isLoading ? (
              <tr>
                <td colSpan={5} className="nsi-table__empty">Loading…</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row.id || row.effectiveDate}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {isFallback ? 'Always' : formatIsoDate(row.effectiveDate)}
                  {row.id != null && row.id === currentId ? (
                    <Tag color="blue" size="sm" className="ms-2">Current</Tag>
                  ) : null}
                  {!isFallback && row.effectiveDate > todayIso ? (
                    <Tag color="orange" size="sm" className="ms-2">Upcoming</Tag>
                  ) : null}
                </td>
                <td className="fw-semibold">{describePolicy(row)}</td>
                <td className="bdc-table-wrap">{row.notes || <span className="nsi-table__muted">—</span>}</td>
                <td className="bdc-table-wrap">
                  {row.createdByName || <span className="nsi-table__muted">Seeded</span>}
                  {row.createdAt ? <span className="nsi-table__muted"> · {formatTimestamp(row.createdAt)}</span> : null}
                </td>
                {canRemove ? (
                  <td className="nsi-table__right">
                    <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => setRemoving(row)}>
                      Remove
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!draft} onClose={() => setDraft(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Add PATO Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {draftShown ? (
            <Form fluid>
              <Form.Group controlId="pato_policy_effective_date">
                <Form.ControlLabel>Effective date</Form.ControlLabel>
                <DatePicker
                  value={draftShown.effectiveDate}
                  onChange={(value) => setDraft((prev) => ({ ...prev, effectiveDate: value }))}
                  format="MM/dd/yyyy"
                  oneTap
                  block
                  cleanable={false}
                  placement="bottomStart"
                />
                <Form.HelpText>January 1 sets that year&rsquo;s grant. A later date posts the difference that day.</Form.HelpText>
              </Form.Group>
              <Form.Group controlId="pato_policy_mode">
                <Form.ControlLabel>Policy</Form.ControlLabel>
                <RadioGroup
                  inline
                  name="pato_policy_mode"
                  value={draftShown.mode}
                  onChange={(value) => setDraft((prev) => ({ ...prev, mode: value }))}
                >
                  <Radio value="hours">Hours a year</Radio>
                  <Radio value="unlimited">Unlimited time off</Radio>
                </RadioGroup>
              </Form.Group>
              {draftShown.mode === 'hours' ? (
                <Form.Group controlId="pato_policy_hours">
                  <Form.ControlLabel>PATO hours preloaded each year</Form.ControlLabel>
                  <InputNumber
                    value={draftShown.hours}
                    onChange={(value) => setDraft((prev) => ({ ...prev, hours: value }))}
                    onFocus={(event) => event.target.select()}
                    min={0}
                    max={2000}
                    step={8}
                    postfix="hours"
                  />
                </Form.Group>
              ) : (
                <Message type="info" showIcon style={{ marginBottom: 16 }}>
                  From this date nobody has a PATO balance. Time off and sick time are still recorded and
                  reported as used.
                </Message>
              )}
              <Form.Group controlId="pato_policy_notes">
                <Form.ControlLabel>Notes</Form.ControlLabel>
                <Input
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={draftShown.notes}
                  onChange={(value) => setDraft((prev) => ({ ...prev, notes: value }))}
                  placeholder="e.g. Adjustment per management directive"
                />
                <Form.HelpText>
                  For a change partway through a year, this is what every employee&rsquo;s PATO report shows for
                  it. Left empty, the report says &ldquo;Adjustment per management directive&rdquo;.
                </Form.HelpText>
              </Form.Group>
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
          <Button variant="primary" loading={addMutation.isPending} disabled={!draftValid} onClick={submitDraft}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        open={!!removing}
        title="Remove PATO Policy"
        body={removing ? (
          <>
            Remove the policy of <strong>{describePolicy(removing)}</strong> from{' '}
            {formatIsoDate(removing.effectiveDate)}? Every balance it affected is worked out again as though it
            had never been entered. Use this to correct a mistake; to change the policy going forward, add a
            new one instead.
          </>
        ) : null}
        confirmText="Remove"
        danger
        loading={removeMutation.isPending}
        onConfirm={submitRemove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}

export default function TimeOffConfigurationPage() {
  const auth = useAuth();
  const canManagePato = PAYROLL_ENABLED && auth.hasPermission(PAYROLL_PERMISSION);

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Configuration', to: '/admin/configuration' },
          { label: 'Time Off' },
        ]}
        icon="bi-airplane"
        title="Time Off Configuration"
      />
      {canManagePato ? <PatoPolicies /> : null}
    </div>
  );
}
